import { Controller } from "@hotwired/stimulus"

// Baustellen bulk-report export dialog.
//
// The site list is server-rendered into the #site_export_picker frame — on first open and
// on every (debounced) search — never with the page. So the selection cannot live in the
// checkboxes: a search replaces them. It lives in `this.selected` (id -> true) and the
// checkboxes are re-synced from it whenever a new result page arrives.
//
// "Alle" has two meanings for the same reason. When every match is on screen it simply
// checks them. When there are more matches than the page shows, it switches to
// select-all-matching mode: the POST then sends `select_all` plus the search term and the
// server resolves the ids from the same scope the picker rendered (Sites::ExportSiteSearch).
// Touching any single checkbox leaves that mode again.
const SEARCH_DEBOUNCE_MS = 250

export default class extends Controller {
  static targets = [
    "loadingOverlay",
    "exportContent",
    "startDate",
    "endDate",
    "picker",
    "pickerMeta",
    "searchInput",
    "siteCheckbox",
    "selectionSummary",
    "errorMessage",
    "errorText",
    "successMessage",
  ]

  static values = {
    pdfZipUrl: String,
    pickerUrl: String,
    maxSites: Number,
    selectedTemplate: String,
    allMatchingTemplate: String,
    tooManyMessage: String,
  }

  // Stimulus starts the target observer BEFORE it calls connect(), so a checkbox that is
  // already in the DOM reaches siteCheckboxTargetConnected first — which happens on every
  // Turbo Drive restore of a snapshot taken after the picker had loaded. The state has to
  // exist by then, so it is built here and not in connect().
  initialize() {
    this.selected = new Set()
    this.allMatching = false
    this.searchTimer = null
    this.pickerLoaded = false
  }

  connect() {
    if (this.hasErrorTextTarget) {
      this.defaultErrorText = this.errorTextTarget.textContent
    }
    this.resetDialogState()
    this.renderSummary()
  }

  disconnect() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  }

  // Fired by dialog-opener's `dialog:open`. Only the first open pays for the fetch;
  // afterwards the frame already holds a result page and the selection with it.
  loadPicker() {
    if (this.pickerLoaded) return

    this.pickerLoaded = true
    this.reloadPicker()
  }

  resetDialogState() {
    if (this.hasLoadingOverlayTarget) this.loadingOverlayTarget.classList.add("hidden")
    if (this.hasSuccessMessageTarget) this.successMessageTarget.classList.add("hidden")
    if (this.hasErrorMessageTarget) this.errorMessageTarget.classList.add("hidden")
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.remove("hidden", "opacity-50", "pointer-events-none")
    }
  }

  searchSites() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => this.reloadPicker(), SEARCH_DEBOUNCE_MS)
  }

  reloadPicker() {
    if (!this.hasPickerTarget) return

    // A new search invalidates "alle Treffer" — it meant the previous result set. Redraw
    // right away: waiting for the frame would leave "Alle Treffer ausgewählt: N" on screen
    // while selectionCount() has already switched over, and a failed frame never redraws.
    this.allMatching = false
    this.renderSummary()
    const url = new URL(this.pickerUrlValue, window.location.origin)
    if (this.searchTerm()) url.searchParams.set("site_query", this.searchTerm())

    // Clearing the search restores the URL the frame already carries, and Turbo ignores an
    // unchanged src — the filtered list would stay on screen. Reload it explicitly.
    if (this.pickerTarget.src === url.toString()) this.pickerTarget.reload()
    else this.pickerTarget.src = url.toString()
  }

  // The summary shows the match count while select-all is on, so it has to follow a new
  // result page in.
  pickerMetaTargetConnected() {
    this.renderSummary()
  }

  // A fresh result page arrives with unchecked boxes — restore what is selected. Stimulus
  // calls this per checkbox as the frame's content is connected.
  siteCheckboxTargetConnected(checkbox) {
    checkbox.checked = this.allMatching || this.selected.has(checkbox.value)
  }

  toggleSite(event) {
    const checkbox = event.target
    // Leaving select-all-matching: what is on screen and checked is the whole selection now.
    if (this.allMatching) {
      this.allMatching = false
      this.selected = new Set(
        this.siteCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value),
      )
    } else if (checkbox.checked) {
      this.selected.add(checkbox.value)
    } else {
      this.selected.delete(checkbox.value)
    }
    this.renderSummary()
  }

  selectAllSites(event) {
    event.preventDefault()
    this.allMatching = this.matchCount() > this.siteCheckboxTargets.length
    // In select-all-matching mode the ids come from the server. Writing the visible page
    // into `selected` too would survive the mode switch (a new search only clears the
    // flag) and export rows the user never ticked.
    if (this.allMatching) this.selected.clear()
    this.siteCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = true
      if (!this.allMatching) this.selected.add(checkbox.value)
    })
    this.renderSummary()
  }

  deselectAllSites(event) {
    event.preventDefault()
    this.allMatching = false
    this.selected.clear()
    this.siteCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = false
    })
    this.renderSummary()
  }

  exportPdfZip(event) {
    event.preventDefault()
    this.performExport(this.pdfZipUrlValue)
  }

  async performExport(url) {
    const count = this.selectionCount()
    if (count === 0) {
      this.showError()
      return
    }
    if (count > this.maxSitesValue) {
      this.showError(this.tooManyMessageValue)
      return
    }

    this.showLoading()
    this.hideError()
    this.hideSuccess()

    const formData = new FormData()
    if (this.allMatching) {
      formData.append("select_all", "1")
      // The query the rendered page belongs to, not what is in the box right now: inside
      // the debounce window those differ, and matchCount() — the number the ceiling was
      // checked against — describes the rendered one.
      formData.append("site_query", this.renderedQuery())
    } else {
      this.selected.forEach((id) => formData.append("site_ids[]", id))
    }
    if (this.hasStartDateTarget && this.startDateTarget.value) {
      formData.append("start_date", this.startDateTarget.value)
    }
    if (this.hasEndDateTarget && this.endDateTarget.value) {
      formData.append("end_date", this.endDateTarget.value)
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: formData,
      })

      const json = await response.json()

      if (json.success) {
        this.showSuccess()
        return
      }

      if (json.error) {
        this.showError(json.error)
        return
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      console.error("Site export failed:", error)
      this.showError()
    } finally {
      this.hideLoading()
    }
  }

  searchTerm() {
    return this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
  }

  // How many sites match the current search in total, not just on this page. Rendered by
  // the picker partial, so it is right for a plain list and for a filtered one.
  matchCount() {
    return this.hasPickerMetaTarget ? Number(this.pickerMetaTarget.dataset.total || 0) : 0
  }

  // The server's echo of the query behind the page on screen — the one matchCount() counts.
  renderedQuery() {
    return this.hasPickerMetaTarget ? this.pickerMetaTarget.dataset.query || "" : ""
  }

  selectionCount() {
    return this.allMatching ? this.matchCount() : this.selected.size
  }

  renderSummary() {
    if (!this.hasSelectionSummaryTarget) return

    const count = this.selectionCount()
    if (count === 0) {
      this.selectionSummaryTarget.textContent = ""
      return
    }

    const template = this.allMatching ? this.allMatchingTemplateValue : this.selectedTemplateValue
    this.selectionSummaryTarget.textContent = template.replace("%{count}", String(count))
  }

  showLoading() {
    if (this.hasLoadingOverlayTarget) this.loadingOverlayTarget.classList.remove("hidden")
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.add("opacity-50", "pointer-events-none")
    }
  }

  hideLoading() {
    if (this.hasLoadingOverlayTarget) this.loadingOverlayTarget.classList.add("hidden")
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.remove("opacity-50", "pointer-events-none")
    }
  }

  showError(message = null) {
    if (this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message || this.defaultErrorText
    }
    if (this.hasErrorMessageTarget) this.errorMessageTarget.classList.remove("hidden")
  }

  hideError() {
    if (this.hasErrorMessageTarget) this.errorMessageTarget.classList.add("hidden")
  }

  showSuccess() {
    if (this.hasSuccessMessageTarget) this.successMessageTarget.classList.remove("hidden")
    if (this.hasExportContentTarget) this.exportContentTarget.classList.add("hidden")
  }

  hideSuccess() {
    if (this.hasSuccessMessageTarget) this.successMessageTarget.classList.add("hidden")
    if (this.hasExportContentTarget) this.exportContentTarget.classList.remove("hidden")
  }
}
